import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import ContactPage from '../../../components/Global/BannerCard/DynamicHeader/ContactPage1';
import MakeTheShift from '../../../components/MakeTheShift/MakeTheShift';
import {
  Briefcase,
  GraduationCap,
  Users2,
  UserCheck,
  Sparkles,
  ShieldCheck,
  Clock4,
} from 'lucide-react';
import OurPartners from '../../../components/Global/BannerCard/PartnerCarousel/PartnerCarousel';
import HighlightText from '../../../components/HighlightText';

const WhoWeServe = () => {
  const navigate = useNavigate();
  const canonicalUrl = `${process.env.REACT_APP_BASE_URL || 'https://www.adithyainfinity.com'}/who-we-serve`;
  const isDevelopment = process.env.NODE_ENV === 'development';

  const audience = [
    {
      title: 'IT Jobs',
      icon: null,
      image: 'https://cdn-icons-png.flaticon.com/512/1055/1055687.png',
      description:
        'From startups to tech giants, we help IT companies hire top-notch developers, testers, and cloud experts efficiently.',
      route: '/careers/open-positions?type=IT',
    },
    {
      title: 'Non-IT Jobs',
      icon: null,
      image: 'https://cdn-icons-png.flaticon.com/512/1995/1995591.png',
      description:
        'We connect non-tech industries—like retail, manufacturing, logistics—with skilled professionals across departments.',
      route: '/careers/open-positions?type=Non-IT',
    },
    {
      title: 'Job Seekers',
      icon: <Briefcase className="w-7 h-7 text-green-500" />,
      image: 'https://cdn-icons-png.flaticon.com/512/1041/1041884.png',
      description:
        'Find jobs that match your profile, get resume tips, and receive alerts for roles that align with your career path.',
      route: '/careers/open-positions',
    },
    {
      title: 'Students & Freshers',
      icon: <GraduationCap className="w-7 h-7 text-purple-500" />,
      image: 'https://cdn-icons-png.flaticon.com/512/3135/3135755.png',
      description:
        'Begin your career journey with internships, training programs, and job listings tailored to students and freshers.',
      route: '/careers/open-positions',
    },
    {
      title: 'Freelancers & Remote Workers',
      icon: <UserCheck className="w-7 h-7 text-cyan-500" />,
      image: 'https://cdn-icons-png.flaticon.com/512/921/921347.png',
      description:
        'Explore flexible opportunities across various domains—tech, design, writing, marketing, and more.',
      route: '/careers/open-positions',
    },
    {
      title: 'Recruiters & Career Services',
      icon: <Users2 className="w-7 h-7 text-pink-500" />,
      image: 'https://cdn-icons-png.flaticon.com/512/4140/4140047.png',
      description:
        'We assist HR professionals, placement officers, and career coaches with recruitment tools and analytics dashboards.',
      route: '/careers/open-positions',
    },
  ];

  const features = [
    {
      title: 'Smart Matching',
      icon: <Sparkles className="w-7 h-7 text-blue-500" />,
      description: 'Our AI instantly matches candidates with jobs based on skills and preferences.',
    },
    {
      title: 'Verified Listings',
      icon: <ShieldCheck className="w-7 h-7 text-emerald-500" />,
      description: 'We ensure all job posts are genuine, verified, and frequently updated.',
    },
    {
      title: '24/7 Support',
      icon: <Clock4 className="w-7 h-7 text-yellow-400" />,
      description: 'Reach our support team anytime for help with applications, profiles, or posts.',
    },
  ];

  const queryParams = new URLSearchParams(window.location.search);
  const highlightWord = queryParams.get('highlight');

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>Adithya Infinity</title>
        <meta
          name="description"
          content="Discover how V Adithya Infinity Pvt Ltd empowers IT firms, non-IT industries, job seekers, freelancers, students, and recruiters through tailored staffing and career services."
        />
        <meta
          name="keywords"
          content="Who we serve, job solutions India, staffing for companies, career services for students, IT recruitment, non-IT hiring, freshers jobs, freelancers platform, HR analytics, job seekers India"
        />
        {isDevelopment ? (
          <meta name="robots" content="noindex, nofollow" />
        ) : (
          <link rel="canonical" href={canonicalUrl} />
        )}
        <meta property="og:title" content="Who We Serve | Job & Career Services | V Adithya Infinity Pvt Ltd" />
        <meta
          property="og:description"
          content="We connect companies, candidates, and communities with tailored career services. Serving job seekers, freelancers, students, and HR teams alike."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://www.adithyainfinity.com/assets/images/who-we-serve-banner.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Who We Serve | V Adithya Infinity Pvt Ltd" />
        <meta
          name="twitter:description"
          content="Career-focused solutions for IT, non-IT companies, students, freelancers, and job seekers across India."
        />
        <meta name="twitter:image" content="https://www.adithyainfinity.com/assets/images/who-we-serve-banner.jpg" />
      </Helmet>

      <div className="bg-white text-black">
        <ContactPage
          title="WHO WE SERVE"
          subTitle="Connecting companies, candidates, and communities with career-focused solutions."
          backgroundImage="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wCEAAkGBw8NDw0NDQ0QDQ0NEBINDQ0NEBANDQ0NFRIWFhURFRUYHSggGBolGxUVITEhJSkrOi4uFx8/OD84Nyg5OisBCgoKDg0NFQ0NFSsZFRkrLS03LSsrKy03Ny0rKystLS03LS0rNy0tLS0tNysrLTc3Ny0tNystKy03Ny0tKy0tK//AABEIAKgBLAMBIgACEQEDEQH/xAAYAAEBAQEBAAAAAAAAAAAAAAAAAQIHA//EACYQAQAABQUAAgMBAQEAAAAAAAADMdHh8AIUQVOSARFRYcGhsSH/xAAVAQEBAAAAAAAAAAAAAAAAAAAAAf/EABQRAQAAAAAAAAAAAAAAAAAAAAD/2gAMAwEAAhEDEQA/AOIAKKACoAigApZFsAAC3LJfktyC3Vm/LWTAuJfkr+QU+/8Av8TJrX8/oEVMmZMD7/gnxn/pkwLFyxcAABAARUAAFQBAAAAUURQAMmCiZMyYirbhMmW5BS3Bky3IBbgyZbkFvwJflcmBklyTOTWv5AyS1/jOTWv5/QGSEyZkwW3CZItyZMC3BfgtyX5AQyZkwATJgqGTBT5D5QABABAUAFEVQABRARSwW5BSxk0tyCljJluQLr9pfkyYH2qZMyYC5/iZMyf6ADJmTAsFuTJgWLluUvyCoZMyYAICoAoCIAAAICgAAAoCioAKBbkQWyZMyYKJk1yYFxL8rkwBMmuTAM/xMmuTAEya5MCwmTMmC5JDJmTADJgCAKAAICAACAAoAAAKIKKAClkAUQEUEBbqyoKIAKgAqAAAAgCoAoAAIAAIAIAioDQn2AoCgAgogCgKKgAomTAFQEUTJgKIuTAEyagCAAZMFAAEVEFQFAABPtUQQABftAGkAFAAABRAFEUAD7UFQAVAAABUAAAAQFQAAEAEBU+w+wPtAAAAABfg+EX4BRPgBRBRQEBUFFEEFEFFEEFEFFEEFEAAAAAAQFD7T7A+flFQAAAAAAAAFEAVWVBRFAAAAAAAAAAAAAAAAABAVBAUQAAAAABAURQAAAAAAFQBVZUFEAURfkARQBFAE+AFEAAAEAFQAAAAAAQAAHvs4vTE8aqGzi9MTxqoAGzi9MTxqouzi9MTxqogC7OL1RPGqhs4vTE8aqABs4vTE8aqGzi9MTxqoAGzi9MTxqobOL0xPGqgAbOL0xPGqhs4vTE8aqABs4vVE8aqGzi9UTxqoAGzi9UTxqobOL1RPGqgAbOL1RPGqhs4vVE8aqABs4vVE8aqGzi9UTxqoAGzi9UTxqobOL1RPGqgAbOL1RPGqhs4vVE8aqABs4vTE8aqGzi9MTxqoAGzi9MTxqobOL0xPGqgAbOL0xPGqhs4vTE8aqABs4vTE8aqGzi9UTxqogC7OL1RPGqibOL0xPGqgAbOL0xPGqhs4vTE8aqABs4vTE8aqGzi9MTxqoAP/9k="
          highlightWord="SERVE"
        />

        <section className="py-20 px-4 sm:px-6 lg:px-20">
          <div className="max-w-7xl mx-auto grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {audience.map((item, index) => (
              <div
                key={index}
                onClick={() => navigate(item.route)}
                className="bg-gray-100 text-black p-6 rounded-2xl shadow-md 
                           hover:shadow-xl hover:scale-105 hover:bg-white 
                           transition-all duration-300 border border-gray-200 
                           hover:border-cyan-500 cursor-pointer"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-20 h-20 mx-auto mb-4 rounded-full object-contain bg-white p-2"
                />
                {item.icon && <div className="mb-2 flex justify-center">{item.icon}</div>}
                <h3 className="text-xl font-semibold mb-2 text-center text-cyan-700 hover:underline">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-700 text-center">{item.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-20 px-6 lg:px-24 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-10">Why Choose Us?</h2>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-100 text-black p-6 rounded-2xl shadow-md hover:shadow-xl 
                           hover:scale-105 transition-all duration-300 border border-gray-200 
                           hover:border-cyan-500 hover:bg-white cursor-pointer"
              >
                <div className="mb-4 flex justify-center">{feature.icon}</div>
                <h4 className="text-xl font-semibold mb-2">{feature.title}</h4>
                <p className="text-sm text-gray-700">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="relative bg-gray-100 text-black py-24 px-8 sm:px-6 lg:px-40 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-5xl font-bold mb-6">Ready to get Started?</h1>
            <h3 className="text-lg sm:text-xl mb-4">
              Post a job, explore opportunities, or create your profile now!
            </h3>
            <Link
              to="/joblist"
              className="inline-block bg-cyan-600 text-white text-sm font-semibold px-6 py-3 rounded-full shadow-md 
                         hover:bg-cyan-700 transition duration-300"
            >
              Get Started
            </Link>
          </div>
        </section>

        <OurPartners />

        <MakeTheShift />
      </div>
    </>
  );
};

export default WhoWeServe;
